import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Video, 
  Clock, 
  User, 
  Briefcase, 
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Play
} from 'lucide-react';
import { CustomVoiceInterview } from '../../components/interview';
import { useAuth } from '../../contexts/AuthContext';
import { FloatingParticles } from '../../components/animations/FloatingParticles';
import apiClient from '../../utils/apiClient';
import jobService, { Job } from '../../services/jobService';

interface UserDetails {
  fullName: string;
  email: string;
  phone: string;
  location: string;
  currentRole: string;
  experience: string;
  motivation: string;
  availability: string;
  skills: string;
  education: string;
  projects: string;
}

const InterviewSession: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  // sessionId param may be either a job ID or session ID depending on how user got here
  const paramValue = sessionId;
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [interviewState, setInterviewState] = useState<'loading' | 'waiting' | 'ready' | 'active' | 'error'>('loading');
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [job, setJob] = useState<Job | null>(null);
  const [jobLoading, setJobLoading] = useState(true);
  const [actualJobId, setActualJobId] = useState<string | null>(null);
  const [actualSessionId, setActualSessionId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [assessmentSections, setAssessmentSections] = useState<any[] | null>(null);

  // Generate interview sections based on job data
  const getInterviewSections = (jobTitle?: string) => [
    {
      title: "Job-Specific Discussion",
      instruction: `You are conducting an interview for ${jobTitle ? `the ${jobTitle} position` : 'a specific job position'}. The candidate has applied for this role and you should:

1. Welcome them and confirm their interest in the position
2. Ask about their relevant experience for this specific role
3. Discuss why they're interested in this particular position
4. Explore their technical skills relevant to the job requirements
5. Ask behavioral questions related to the role

Keep the conversation natural, engaging, and focused on the job they applied for. Don't make it too formal - think of it as a friendly professional conversation.

PATIENCE & ACCOMMODATION GUIDELINES:
- Give candidates 15-20 seconds to think before asking if they need more time
- If you can't hear clearly, say "I didn't catch that clearly, could you repeat that?"
- Use encouraging phrases: "Take your time to think about that", "No rush at all"
- If a candidate seems to struggle with audio, offer alternatives: "Should we try again?" or "Let me rephrase that question"
- Allow natural pauses - silence is okay and shows they're thinking carefully
- Be understanding if they need clarification: "Would you like me to rephrase the question?"

If the candidate asks to end the interview or says they're ready to finish, respond positively by saying something like "Thank you for your time today! It was great learning about your background and interest in this position. We'll be in touch soon regarding the next steps. Have a great day!" Then allow the interview to end naturally.`,
      duration: 300, // 5 minutes - can be longer for job interviews
      questions: [
        "Tell me about your interest in this specific position",
        "What experience do you have that's most relevant to this role?",
        "What attracts you to our company?",
        "How do you handle challenges in your work?",
        "What are your career goals?"
      ]
    },
    {
      title: "Technical Assessment",
      instruction: `Focus on technical skills and problem-solving abilities relevant to the job position:

1. Ask about their technical expertise in areas mentioned in their application
2. Discuss specific projects or achievements
3. Present hypothetical scenarios they might face in this role
4. Explore their learning approach and adaptability
5. Understand their collaboration and communication style

Keep the conversation flowing naturally while gathering insights about their capabilities.

TECHNICAL QUESTION PATIENCE:
- Technical questions need thinking time - give candidates 20-30 seconds to formulate responses
- For complex scenarios, say: "This is a technical question, so take your time to think through your approach"
- If they start and stop, encourage them: "You're doing great, take your time to organize your thoughts"
- Offer to clarify: "Would it help if I break that question down into smaller parts?"
- Be patient with detailed explanations - technical topics require thorough responses
- If they seem stuck, offer: "Would you like to approach this from a different angle?" or "What part would you like to start with?"`,
      duration: 600, // 10 minutes
      questions: [
        "Walk me through a challenging project you've worked on",
        "How do you approach learning new technologies?",
        "Describe your problem-solving process",
        "How do you handle working in a team?",
        "What tools and technologies are you most comfortable with?"
      ]
    }
  ];

  // Fetch assessment for the job
  const fetchAssessmentForJob = async (jobId: string) => {
    try {
      const response = await apiClient.get(`/assessments/by-job/${jobId}`);

      if (response.data?.success && response.data?.data?.sections) {
        setAssessmentSections(response.data.data.sections);
        return response.data.data.sections;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  };

  // Define loadUserProfile function outside useEffect so it can be reused
  const loadUserProfile = async () => {
    // Wait for auth loading to complete before checking authentication
    if (authLoading) {
      return;
    }

    if (!isAuthenticated || !user) {
      navigate('/login', { replace: true, state: { from: `/interview/${paramValue}` } });
      return;
    }

    try {
      setProfileLoading(true);
      setJobLoading(true);

      // Fetch user profile first
      const profileResponse = await apiClient.get('/auth/me');
      
      // The param is a session ID - get the interview session to find job info
      let jobData: Job | null = null;
      let resolvedJobId: string | null = null;

      try {
        // Get interview sessions to find the one that matches
        const sessionsResponse = await apiClient.get('/interviews/');
        const sessions = sessionsResponse.data.data?.sessions || [];

        // Find the session that matches this session ID
        const matchingSession = sessions.find((s: any) => s.id === paramValue);
        
        if (matchingSession) {
          resolvedJobId = matchingSession.job_id || matchingSession.jobId;

          // Store the actual session ID for API calls
          setActualSessionId(matchingSession.id);

          // Create job object from session data (which includes job title from the JOIN)
          jobData = {
            id: resolvedJobId || '',
            title: matchingSession.job_title || matchingSession.jobTitle || 'Position',
            department: matchingSession.department || 'Unknown',
            description: `Interview session for ${matchingSession.job_title || 'this position'}`,
            requirements: '',
            location: '',
            jobType: 'Full-time',
            level: '',
            postedDate: '',
            closingDate: ''
          };
        } else {
          // Fallback: try to treat param as job ID (backward compatibility)
          // Find session by job ID instead
          const sessionByJobId = sessions.find((s: any) =>
            (s.job_id || s.jobId) === paramValue
          );

          if (sessionByJobId) {
            resolvedJobId = paramValue || '';
            setActualSessionId(sessionByJobId.id);  // Use the actual session ID

            // Create job object with session data
            jobData = {
              id: resolvedJobId,
              title: sessionByJobId.job_title || sessionByJobId.jobTitle || 'Position',
              department: sessionByJobId.department || 'Unknown',
              description: `Interview session for ${sessionByJobId.job_title || 'this position'}`,
              requirements: '',
              location: '',
              jobType: 'Full-time',
              level: '',
              postedDate: '',
              closingDate: ''
            };
          } else {
            // Ultimate fallback - try to get job data directly
            try {
              jobData = await jobService.getJobById(paramValue || '');
              resolvedJobId = paramValue || '';
              // No session ID in this case - interview completion will fail
              setActualSessionId(null);
            } catch (fallbackError) {
              throw new Error(`No interview session found for session ID: ${paramValue}`);
            }
          }
        }
      } catch (error) {
        throw error;
      }

      const response = profileResponse;

      // Fix: The backend returns data directly, not nested under data.profile
      const profileData = response.data.data || {};

      // Map the backend profile data to UserDetails interface with proper null checks
      const userDetailsData: UserDetails = {
        fullName: profileData?.name || user?.name || 'Unknown User',
        email: profileData?.email || user?.email || '',
        phone: profileData?.phone || '',
        location: profileData?.location || '',
        currentRole: profileData?.title || '',
        experience: Array.isArray(profileData?.experience)
          ? profileData.experience.map((exp: any) => `${exp.company || 'Company'} - ${exp.position || 'Position'} (${exp.duration || 'Duration'})`).join('\n')
          : profileData?.experience || '',
        motivation: profileData?.about || '',
        availability: 'Immediate', // Default value since this isn't typically stored
        skills: Array.isArray(profileData?.skills)
          ? profileData.skills.join(', ')
          : profileData?.skills || '',
        education: profileData?.education || '',
        projects: '' // Default empty since this isn't typically stored in profile
      };

      setUserDetails(userDetailsData);
      setJob(jobData);
      setActualJobId(resolvedJobId);

      // Fetch assessment for this job
      if (resolvedJobId) {
        await fetchAssessmentForJob(resolvedJobId);
      }

      // Set to ready state to show pre-interview screen first
      setInterviewState('ready');

    } catch (error: any) {
      setErrorMessage('Failed to load interview session data. Please try again.');
      setInterviewState('error');

      // Fallback to basic user data from AuthContext with proper null checks
      const basicUserDetails: UserDetails = {
        fullName: user?.name || 'Unknown User',
        email: user?.email || '',
        phone: user?.phone || '',
        location: '',
        currentRole: '',
        experience: '',
        motivation: '',
        availability: 'Immediate',
        skills: '',
        education: '',
        projects: ''
      };
      
      // Try to load job data from interview sessions since profile loading failed
      try {
        const sessionsResponse = await apiClient.get('/interviews/');
        const sessions = sessionsResponse.data.data?.sessions || [];
        const matchingSession = sessions.find((s: any) => s.id === paramValue);
        
        if (matchingSession) {
          const fallbackJobData = {
            id: matchingSession.job_id || matchingSession.jobId,
            title: matchingSession.job_title || matchingSession.jobTitle || 'Position',
            department: matchingSession.department || 'Unknown',
            description: `Interview session for ${matchingSession.job_title || 'this position'}`
          } as any;

          setJob(fallbackJobData);
          setActualJobId(matchingSession.job_id || matchingSession.jobId);
          setActualSessionId(matchingSession.id);  // Store session ID
        } else {
          // Look for session by job ID in fallback
          const sessionByJobId = sessions.find((s: any) =>
            (s.job_id || s.jobId) === paramValue
          );

          if (sessionByJobId) {
            setJob({
              id: paramValue || '',
              title: sessionByJobId.job_title || sessionByJobId.jobTitle || 'Interview Session',
              department: sessionByJobId.department || 'Unknown',
              description: `Interview session for ${sessionByJobId.job_title || 'this position'}`
            } as any);
            setActualJobId(paramValue || '');
            setActualSessionId(sessionByJobId.id);
          } else {
            // Ultimate fallback
            setJob({
              id: paramValue || '',
              title: 'Interview Session',
              department: 'Unknown',
              description: 'Interview session'
            } as any);
            setActualJobId(paramValue || '');
            setActualSessionId(null);
          }
        }
      } catch (jobError) {
        // Use basic info
        setJob({
          id: paramValue || '',
          title: 'Interview Session',
          department: 'Unknown',
          description: 'Interview session'
        } as any);
        setActualJobId(paramValue || '');
        setActualSessionId(null);  // No session ID available
      }
      
      setUserDetails(basicUserDetails);
      setInterviewState('ready');
    } finally {
      setProfileLoading(false);
      setJobLoading(false);
    }
  };

  // Fetch user profile data on component mount
  useEffect(() => {

    loadUserProfile();
  }, [isAuthenticated, user, paramValue, navigate, authLoading]);

  const handleStartInterview = () => {
    setInterviewState('active');
  };

  const handleRetryLoad = () => {
    setErrorMessage(null);
    setInterviewState('loading');
    // Reload the user profile
    if (isAuthenticated && user) {
      loadUserProfile();
    }
  };

  const handleEndInterview = async () => {
    if (actualJobId) {
      try {
        // Note: We don't automatically set status to 'interviewed' here because
        // the interview might have been canceled rather than completed.
        // The CustomVoiceInterview component should handle status updates
        // based on whether the interview was actually completed or abandoned.
      } catch (error) {
        // Error handling
      }
    }

    // Navigate back to dashboard with refresh state to ensure data is reloaded
    navigate('/dashboard', {
      replace: true,
      state: {
        refreshFromInterview: true,
        interviewSessionId: paramValue,
        timestamp: Date.now()
      }
    });
  };

  // Loading screen component
  if (authLoading || interviewState === 'loading' || profileLoading || jobLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center relative overflow-hidden">
        <FloatingParticles />
        
        <motion.div 
          initial={{ opacity: 0, scale: 0.8, y: 50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10 bg-white/10 backdrop-blur-2xl border border-white/20 rounded-3xl p-12 shadow-2xl"
        >
          <div className="text-center">
            {/* Loading Icon */}
            <div className="w-20 h-20 bg-gradient-to-br from-gray-900/90 to-black/95 backdrop-blur-2xl rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-black/80 ring-1 ring-white/10">
              <div className="w-10 h-10 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
            </div>
            
            {/* Loading Text */}
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-white via-amber-100 to-yellow-200 bg-clip-text text-transparent mb-4 leading-tight"
            >
              Preparing Your Interview
            </motion.h2>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="space-y-3"
            >
              <p className="text-gray-300 text-lg">Setting up your AI-powered interview session</p>
              <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
                <img src="/assets/images/branding/Website-Icon.png" alt="MTAI Logo" className="w-4 h-4" />
                <span>Loading your profile and job details...</span>
              </div>
            </motion.div>
            
            {/* Progress Indicators */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="mt-8 flex items-center justify-center gap-2"
            >
              {[0, 1, 2].map((index) => (
                <motion.div
                  key={index}
                  className="w-2 h-2 bg-amber-400/50 rounded-full"
                  animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: index * 0.2,
                  }}
                />
              ))}
            </motion.div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Error state
  if (interviewState === 'error') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center relative overflow-hidden">
        <FloatingParticles />
        <motion.div 
          initial={{ opacity: 0, scale: 0.8, y: 50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10 bg-white/10 backdrop-blur-2xl border border-white/20 rounded-3xl p-12 shadow-2xl text-center max-w-md w-full mx-4"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-gray-900/90 to-black/95 backdrop-blur-2xl rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-black/80 ring-1 ring-white/10">
            <AlertCircle className="w-10 h-10 text-red-300" />
          </div>
          
          <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-white via-red-100 to-red-200 bg-clip-text text-transparent mb-4">
            Unable to Load Interview
          </h2>
          
          <p className="text-gray-300 text-lg mb-8">
            {errorMessage || 'We encountered an issue loading your interview session. Please try again.'}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              onClick={handleRetryLoad}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-semibold rounded-2xl hover:from-amber-400 hover:to-yellow-400 transition-all duration-300 shadow-lg"
            >
              <RefreshCw className="w-5 h-5" />
              Try Again
            </motion.button>
            
            <motion.button
              onClick={() => navigate('/dashboard', { replace: true })}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-xl border border-white/20 text-white font-semibold rounded-2xl hover:bg-white/20 transition-all duration-300"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Dashboard
            </motion.button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Ready state - pre-interview preparation screen
  if (interviewState === 'ready') {
    return (
      <div className="min-h-screen bg-black relative overflow-hidden">
        <FloatingParticles />
        
        {/* Smart Back Button */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-8">
          <motion.button
            onClick={() => navigate('/dashboard', { replace: true })}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            whileHover={{ scale: 1.02, y: -1 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex items-center gap-3 bg-white/5 backdrop-blur-2xl border border-white/10 rounded-xl px-4 py-3 hover:bg-white/[0.08] hover:border-white/20 transition-all duration-300 group shadow-lg"
          >
            <div className="w-8 h-8 bg-white/10 backdrop-blur-xl rounded-lg flex items-center justify-center border border-white/15 group-hover:bg-white/15 group-hover:border-white/25 transition-all duration-200">
              <ArrowLeft className="w-4 h-4 text-gray-300 group-hover:text-white transition-colors duration-200" />
            </div>
            <div className="flex flex-col items-start">
              <span className="text-white text-sm font-semibold leading-tight">Back to Dashboard</span>
              <span className="text-gray-400 text-xs leading-tight group-hover:text-gray-300 transition-colors duration-200">
                View your applications
              </span>
            </div>
          </motion.button>
        </div>

        {/* Interview Preparation Screen */}
        {job && userDetails && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 mt-8"
          >
            <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl sm:rounded-3xl p-6 sm:p-10 shadow-xl">
              {/* Header */}
              <div className="text-center mb-10">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 }}
                  className="w-24 h-24 bg-gradient-to-br from-gray-900/90 to-black/95 backdrop-blur-2xl rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-black/80 ring-1 ring-white/10"
                >
                  <Video className="w-12 h-12 text-amber-300" />
                </motion.div>
                
                <motion.h1 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-white via-amber-100 to-yellow-200 bg-clip-text text-transparent mb-4 leading-tight"
                >
                  Ready to Start Your Interview
                </motion.h1>
                
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="flex flex-wrap justify-center gap-3 mb-6"
                >
                  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 backdrop-blur-sm border border-amber-400/30 rounded-full px-4 py-2 text-amber-200">
                    <Briefcase className="w-4 h-4" />
                    <span className="text-sm font-medium">{job.title}</span>
                  </div>
                  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full px-4 py-2 text-blue-200">
                    <User className="w-4 h-4" />
                    <span className="text-sm font-medium">{userDetails.fullName}</span>
                  </div>
                </motion.div>
              </div>

              {/* Interview Details Grid */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="grid md:grid-cols-2 gap-6 mb-10"
              >
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-gray-900/90 to-black/95 backdrop-blur-2xl rounded-full flex items-center justify-center shadow-2xl shadow-black/80 ring-1 ring-white/10">
                      <Video className="w-6 h-6 text-green-300" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg">AI-Powered Interview</h3>
                      <p className="text-gray-400 text-sm">Interactive conversation assessment</p>
                    </div>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Experience a natural conversation with our advanced AI interviewer that adapts to your responses and provides real-time engagement.
                  </p>
                </div>

                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-gray-900/90 to-black/95 backdrop-blur-2xl rounded-full flex items-center justify-center shadow-2xl shadow-black/80 ring-1 ring-white/10">
                      <Clock className="w-6 h-6 text-purple-300" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg">15-20 Minutes</h3>
                      <p className="text-gray-400 text-sm">Flexible duration</p>
                    </div>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    The interview adapts to the flow of conversation. Take your time to think through responses - there's no strict time limit per question.
                  </p>
                </div>
              </motion.div>

              {/* Pre-Interview Checklist */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="bg-gradient-to-br from-amber-500/10 via-yellow-500/5 to-amber-600/10 backdrop-blur-sm border border-amber-400/20 rounded-2xl p-8 mb-8"
              >
                <h3 className="text-xl font-bold text-white mb-6 text-center">Before You Begin</h3>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { icon: Video, text: 'Ensure your camera and microphone are working' },
                    { icon: Clock, text: 'Find a quiet space with good lighting' },
                    { icon: User, text: 'Have your resume or key experiences in mind' },
                    { icon: CheckCircle, text: 'Relax and be yourself - this is a conversation!' }
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.8 + index * 0.1 }}
                      className="flex items-center gap-3 text-amber-200"
                    >
                      <div className="w-8 h-8 bg-amber-400/20 backdrop-blur-xl rounded-full flex items-center justify-center border border-amber-400/30 flex-shrink-0">
                        <item.icon className="w-4 h-4 text-amber-300" />
                      </div>
                      <span className="text-sm">{item.text}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Start Interview Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                className="text-center"
              >
                <motion.button
                  onClick={handleStartInterview}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center gap-4 px-8 py-4 bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-bold text-lg rounded-2xl hover:from-amber-400 hover:to-yellow-400 transition-all duration-300 shadow-xl hover:shadow-amber-500/25"
                >
                  <Play className="w-6 h-6" />
                  Start Interview
                </motion.button>
                
                <p className="text-gray-400 text-sm mt-4">
                  Your interview will be recorded for assessment purposes
                </p>
              </motion.div>
            </div>
          </motion.div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      <FloatingParticles />
      
      {/* Smart Back Button */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-8">
        <motion.button
          onClick={() => navigate('/dashboard', { replace: true })}
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          whileHover={{ scale: 1.02, y: -1 }}
          whileTap={{ scale: 0.98 }}
          className="inline-flex items-center gap-3 bg-white/5 backdrop-blur-2xl border border-white/10 rounded-xl px-4 py-3 hover:bg-white/[0.08] hover:border-white/20 transition-all duration-300 group shadow-lg"
        >
          <div className="w-8 h-8 bg-white/10 backdrop-blur-xl rounded-lg flex items-center justify-center border border-white/15 group-hover:bg-white/15 group-hover:border-white/25 transition-all duration-200">
            <ArrowLeft className="w-4 h-4 text-gray-300 group-hover:text-white transition-colors duration-200" />
          </div>
          <div className="flex flex-col items-start">
            <span className="text-white text-sm font-semibold leading-tight">Back to Dashboard</span>
            <span className="text-gray-400 text-xs leading-tight group-hover:text-gray-300 transition-colors duration-200">
              View your applications
            </span>
          </div>
        </motion.button>
      </div>

      {/* Interview Header Card */}
      {job && userDetails && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 mt-8 mb-12"
        >
          <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl sm:rounded-3xl p-6 sm:p-10 shadow-xl hover:shadow-2xl hover:bg-white/[0.07] hover:border-white/15 transition-all duration-300">
            {/* Job Title & Info */}
            <div className="mb-8">
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-3xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-white via-amber-100 to-yellow-200 bg-clip-text text-transparent mb-4 leading-tight"
              >
                Interview for {job.title}
              </motion.h1>
              
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex flex-wrap gap-3 mb-6"
              >
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 backdrop-blur-sm border border-amber-400/30 rounded-full px-4 py-2 text-amber-200">
                  <Briefcase className="w-4 h-4" />
                  <span className="text-sm font-medium">{job.department}</span>
                </div>
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full px-4 py-2 text-blue-200">
                  <User className="w-4 h-4" />
                  <span className="text-sm font-medium">{userDetails.fullName}</span>
                </div>
              </motion.div>
            </div>

            {/* Interview Details */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="grid md:grid-cols-2 gap-6 mb-8"
            >
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-gray-900/90 to-black/95 backdrop-blur-2xl rounded-full flex items-center justify-center shadow-2xl shadow-black/80 ring-1 ring-white/10">
                    <Video className="w-5 h-5 text-green-300" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">AI-Powered Interview</h3>
                    <p className="text-gray-400 text-sm">Live conversation assessment</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-gray-900/90 to-black/95 backdrop-blur-2xl rounded-full flex items-center justify-center shadow-2xl shadow-black/80 ring-1 ring-white/10">
                    <Clock className="w-5 h-5 text-purple-300" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Duration: 15-20 minutes</h3>
                    <p className="text-gray-400 text-sm">Flexible timing based on discussion</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Ready to Start CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="text-center bg-gradient-to-br from-amber-500/10 via-yellow-500/5 to-amber-600/10 backdrop-blur-sm border border-amber-400/20 rounded-2xl p-6"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-gray-900/90 to-black/95 backdrop-blur-2xl rounded-full flex items-center justify-center mx-auto mb-4 shadow-2xl shadow-black/80 ring-1 ring-white/10">
                <CheckCircle className="w-8 h-8 text-amber-300" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Everything is Ready!</h3>
              <p className="text-gray-300 text-sm mb-4">
                Your interview session has been prepared. Click below to begin your AI-powered interview.
              </p>
              <div className="inline-flex items-center gap-2 text-amber-200 text-xs">
                <img src="/assets/images/branding/Website-Icon.png" alt="MTAI Logo" className="w-4 h-4" />
                <span>Powered by MTAI Recruitment AI</span>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}

      {/* Interview Component */}
      {interviewState === 'active' && userDetails && (
        <div className="relative z-20">
          <CustomVoiceInterview
            onEndInterview={handleEndInterview}
            sections={assessmentSections || getInterviewSections(job?.title)}
            interviewName={job?.title ? `Interview for ${job.title}` : `Interview for Position`}
            description="AI-powered job interview assessment"
            maxDuration={20}
            userName={userDetails.fullName}
            aiName="MTAI Recruitment AI"
            
            // Use environment variables for configuration
            pipecatApiUrl={import.meta.env.VITE_PIPECAT_API_URL}
            agentName={import.meta.env.VITE_PIPECAT_AGENT_NAME}
            authToken={import.meta.env.VITE_PIPECAT_AUTH_TOKEN}
            s3BucketName={import.meta.env.VITE_AWS_S3_BUCKET_NAME}
            s3BucketRegion={import.meta.env.VITE_AWS_S3_BUCKET_REGION}
            awsAssumeRoleArn={import.meta.env.VITE_AWS_ASSUME_ROLE_ARN}
            fastapiURL={import.meta.env.VITE_PIPECAT_CALLBACK_URL || 'https://nddwrvn1-5000.asse.devtunnels.ms/api'}
            
            // Pass user details
            userId={`job-applicant-${actualJobId}`}
            userCV={`Name: ${userDetails.fullName}
Email: ${userDetails.email}
Phone: ${userDetails.phone}
Location: ${userDetails.location}
Current Role: ${userDetails.currentRole}
Experience: ${userDetails.experience}
Motivation: ${userDetails.motivation}
Skills: ${userDetails.skills}
Education: ${userDetails.education}
Projects: ${userDetails.projects}
Availability: ${userDetails.availability}`}
            
            interviewId={`job-${actualJobId}-${Date.now()}`}
            sessionId={actualSessionId || undefined}  // Pass the actual session ID
            jobId={actualJobId || undefined}
            language="English"
            interviewLanguage="en-US"
            voice="male"
            customGreeting={`Hello ${userDetails.fullName}! Welcome to your interview${job?.title ? ` for the ${job.title} position` : ' for this position'}. I'm excited to learn more about your background and discuss how you might fit into our team.`}
          />
        </div>
      )}
    </div>
  );
};

export default InterviewSession;